import { Banner } from "@mittwald/flow-components/dist/components/Banner";
import { Box } from "@mittwald/flow-components/dist/components/Box";
import { Button } from "@mittwald/flow-components/dist/components/Button";
import { ColumnLayout } from "@mittwald/flow-components/dist/components/ColumnLayout";
import Form, { useForm } from "@mittwald/flow-components/dist/components/Form";
import { H1 } from "@mittwald/flow-components/dist/components/H1";
import { Image } from "@mittwald/flow-components/dist/components/Image";
import { Section } from "@mittwald/flow-components/dist/components/Section";
import { Text } from "@mittwald/flow-components/dist/components/Text";
import { TextField } from "@mittwald/flow-components/dist/components/TextField";
import { useTranslation } from "@mittwald/flow-components/dist/hooks/useTranslation";
import { extractTextFromReactNode } from "@mittwald/flow-components/dist/lib/extractTextFromReactNode";
import React, { useState } from "react";
import z from "zod";
import { mittwaldApi } from "../../../api/Mittwald";
import { ConditionsCheckbox } from "../../../components/ConditionsCheckbox/ConditionsCheckbox";
import { ContentBox } from "../../components/styled";
import { withValidatedProps } from "../../withValidatedProps";
import image from "./img/relocation.svg";

interface FormType {
  accountNumber: string;
}

export default withValidatedProps(
  z.object({
    newTarifVariant: z.string(),
  }),
  (props) => {
    const { newTarifVariant } = props;

    const translation = useTranslation();

    const [error, setError] = useState<string | undefined>(undefined);
    const [successfullySubmitted, setSuccessfullySubmitted] = useState<
      boolean | undefined
    >(undefined);
    const form = useForm<FormType>({
      defaultValues: {
        accountNumber: "",
      },

      onSubmit: async (values) => {
        setSuccessfullySubmitted(undefined);
        setError(undefined);

        const result =
          await mittwaldApi.relocationCreateLegacyTariffChange.request({
            requestBody: {
              pAccount: values.accountNumber,
              targetTariff: newTarifVariant,
            },
          });

        if (result.status === 201) {
          setSuccessfullySubmitted(true);
        } else {
          setSuccessfullySubmitted(false);
          setError(
            result.content.message ||
              extractTextFromReactNode(
                translation("orderTarifChangeWidget.unknownError"),
              ),
          );
        }
      },
    });

    form.watch(() => {
      if (!form.formState.isSubmitSuccessful) {
        if (error !== undefined) {
          setError(undefined);
        }
      }
    });

    const successScreen = successfullySubmitted && (
      <Box _alignItems="center" _flexDirection="column">
        <Box
          _alignItems="center"
          _flexDirection="column"
          _itemGap="m"
          _maxW={430}
        >
          <Image _height="auto" _width={230} url={image} />
          <H1 i18n="orderCompleteHeadline" />
          <Text center i18n="orderCompleteDescription" />
        </Box>
      </Box>
    );

    const errorBanner = successfullySubmitted === false && error && (
      <Banner
        description={{
          text: error,
        }}
        error
        headline="error"
      />
    );

    const formScreen = successfullySubmitted !== true && (
      <Section.Layout>
        <Section.Item headline="widget">
          {errorBanner}
          <Box _flexDirection="column" _itemGap="xl">
            <ColumnLayout _width="100%" medium={[2, 1]}>
              <Box _itemGap="xl">
                <Text i18n="informationText" />
                <Box _width="50%">
                  <TextField
                    infoMessage={{ message: "infoMessage" }}
                    label="accountNumber"
                    name="accountNumber"
                    placeholder="placeholder"
                    rules={{
                      required: true,
                      pattern: /^[pr][0-9]{4,8}$/,
                    }}
                  />
                </Box>
              </Box>
              <Box
                _alignContent="center"
                _display="flex"
                _justifyContent="center"
              >
                <Image _height="auto" _width={230} url={image} />
              </Box>
            </ColumnLayout>
            <ConditionsCheckbox showPHPHint />
            <Box _display="flex" _justifyContent="flex-end">
              <Button
                action={{
                  form: form,
                }}
                ok
                text="bookNow"
              />
            </Box>
          </Box>
        </Section.Item>
      </Section.Layout>
    );

    return (
      <Form controller={form}>
        <ContentBox>
          {successScreen}
          {formScreen}
        </ContentBox>
      </Form>
    );
  },
);
